import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  Search,
  Shield,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Calendar,
  Loader2,
  AlertCircle,
  Check,
  KeyRound,
  Crown,
  Briefcase,
  Link
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';
import AdminResetPasswordModal from '../components/auth/AdminResetPasswordModal';

const ROLES = [
  { value: 'principal', label: 'Principal (Owner)', color: 'bg-purple-500' },
  { value: 'business_head', label: 'Business Head', color: 'bg-blue-500' },
  { value: 'rm', label: 'Relationship Manager', color: 'bg-violet-500' },
  { value: 'dealer', label: 'Dealer', color: 'bg-green-500' },
  { value: 'backoffice', label: 'Back Office', color: 'bg-amber-500' },
];

// Allowed dual role combinations
const DUAL_ROLE_ALLOWED = ['rm', 'dealer'];

const StaffManagementPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('staff');
  const [staff, setStaff] = useState([]);
  const [rms, setRms] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rmFilter, setRmFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    roles: ['dealer'],
    rm_code: '',
    dealer_code: '',
    reporting_rm_id: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStaff(data);
      } else {
        toast.error('Failed to load staff list');
      }
    } catch (err) {
      toast.error('Error loading staff');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRms = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/rms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRms(data);
      }
    } catch (err) {
      console.error('Error loading RMs:', err);
    }
  }, [token]);

  const fetchDealers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/dealers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDealers(data);
      }
    } catch (err) {
      console.error('Error loading dealers:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchStaff();
    fetchRms();
    fetchDealers();
  }, [fetchStaff, fetchRms, fetchDealers]);

  const handleAddStaff = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.roles || formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      toast.error('Mobile number must be exactly 10 digits');
      return;
    }

    if (formData.roles.includes('rm') && !formData.rm_code) {
      toast.error('RM Code is required for Relationship Manager role');
      return;
    }

    if (formData.roles.includes('dealer') && !formData.dealer_code) {
      toast.error('Dealer Code is required for Dealer role');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          role: formData.roles[0],
          roles: formData.roles,
          rm_code: formData.rm_code || null,
          dealer_code: formData.dealer_code || null,
          reporting_rm_id: formData.reporting_rm_id || null,
        }),
      });

      if (res.ok) {
        toast.success(`${formData.name} added successfully`);
        setShowAddDialog(false);
        setFormData({ name: '', email: '', password: '', phone: '', roles: ['dealer'], rm_code: '', dealer_code: '', reporting_rm_id: '' });
        fetchStaff();
        fetchRms();
        fetchDealers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add staff');
      }
    } catch (err) {
      toast.error('Error adding staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStaff = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.roles || formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      toast.error('Mobile number must be exactly 10 digits');
      return;
    }

    setSubmitting(true);
    try {
      const updateData = { 
        ...formData,
        role: formData.roles[0],
        roles: formData.roles,
        reporting_rm_id: formData.reporting_rm_id || null,
      };
      if (!updateData.password) {
        delete updateData.password;
      }

      const res = await fetch(`${API_URL}/staff/${selectedStaff.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (res.ok) {
        toast.success(`${formData.name} updated successfully`);
        setShowEditDialog(false);
        setSelectedStaff(null);
        fetchStaff();
        fetchRms();
        fetchDealers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update staff');
      }
    } catch (err) {
      toast.error('Error updating staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (staffMember) => {
    try {
      const res = await fetch(`${API_URL}/staff/${staffMember.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !staffMember.is_active }),
      });

      if (res.ok) {
        toast.success(`${staffMember.name} ${staffMember.is_active ? 'deactivated' : 'activated'}`);
        fetchStaff();
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const handleDeleteStaff = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/staff/${selectedStaff.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success(`${selectedStaff.name} deleted`);
        setShowDeleteDialog(false);
        setSelectedStaff(null);
        fetchStaff();
        fetchRms();
        fetchDealers();
      } else {
        toast.error('Failed to delete staff');
      }
    } catch (err) {
      toast.error('Error deleting staff');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    const staffRoles = staffMember.roles || [staffMember.role];
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      password: '',
      phone: staffMember.phone || '',
      roles: staffRoles,
      rm_code: staffMember.rm_code || '',
      dealer_code: staffMember.dealer_code || '',
      reporting_rm_id: staffMember.reporting_rm_id || '',
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setShowDeleteDialog(true);
  };

  const getRoleBadges = (staffMember) => {
    const roles = staffMember.roles || [staffMember.role];
    return (
      <div className="flex flex-wrap gap-1">
        {roles.map((role) => {
          const roleConfig = ROLES.find((r) => r.value === role);
          return (
            <Badge key={role} className={`${roleConfig?.color || 'bg-slate-500'} text-white text-xs`}>
              {roleConfig?.label?.replace(' (Owner)', '') || role}
            </Badge>
          );
        })}
      </div>
    );
  };

  const handleRoleToggle = (roleValue) => {
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      
      if (!DUAL_ROLE_ALLOWED.includes(roleValue)) {
        if (currentRoles.includes(roleValue)) {
          const newRoles = currentRoles.filter(r => r !== roleValue);
          return { ...prev, roles: newRoles.length > 0 ? newRoles : [roleValue] };
        } else {
          return { ...prev, roles: [roleValue] };
        }
      }
      
      if (currentRoles.includes(roleValue)) {
        const newRoles = currentRoles.filter(r => r !== roleValue);
        return { ...prev, roles: newRoles.length > 0 ? newRoles : ['dealer'] };
      } else {
        const newRoles = currentRoles.filter(r => DUAL_ROLE_ALLOWED.includes(r));
        newRoles.push(roleValue);
        return { ...prev, roles: newRoles };
      }
    });
  };

  const filteredStaff = staff.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email.toLowerCase().includes(searchTerm.toLowerCase());
    const staffRoles = s.roles || [s.role];
    const matchesRole = roleFilter === 'all' || staffRoles.includes(roleFilter);
    return matchesSearch && matchesRole;
  });

  const filteredRms = rms.filter((rm) => {
    const matchesSearch =
      rm.rm_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rm.email && rm.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || rm.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredDealers = dealers.filter((d) => {
    const matchesSearch =
      d.dealer_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.email && d.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    const matchesRm = rmFilter === 'all' || 
      (rmFilter === 'unassigned' && !d.reporting_rm_id) ||
      d.reporting_rm_id === rmFilter;
    return matchesSearch && matchesStatus && matchesRm;
  });

  const staffStats = {
    total: staff.length,
    active: staff.filter((s) => s.is_active).length,
    dealers: staff.filter((s) => {
      const roles = s.roles || [s.role];
      return roles.includes('dealer') || roles.includes('rm');
    }).length,
    inactive: staff.filter((s) => !s.is_active).length,
  };

  const rmStats = {
    total: rms.length,
    active: rms.filter((rm) => rm.status === 'Active').length,
    totalDealers: rms.reduce((sum, rm) => sum + (rm.dealer_count || 0), 0),
    linked: rms.filter((rm) => rm.linked_user_id).length,
  };

  const dealerStats = {
    total: dealers.length,
    active: dealers.filter((d) => d.status === 'Active').length,
    withRm: dealers.filter((d) => d.reporting_rm_id).length,
    linked: dealers.filter((d) => d.linked_user_id).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-400" />
            Staff Management
          </h1>
          <p className="text-slate-400 mt-1">Manage staff, RMs, and dealers in one place</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Staff Member
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-800/50 border border-slate-700">
          <TabsTrigger 
            value="staff" 
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-400"
            data-testid="tab-staff-directory"
          >
            <Users className="w-4 h-4 mr-2" />
            Staff Directory
          </TabsTrigger>
          <TabsTrigger 
            value="rms" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-400"
            data-testid="tab-rm-management"
          >
            <Crown className="w-4 h-4 mr-2" />
            RM Management
          </TabsTrigger>
          <TabsTrigger 
            value="dealers" 
            className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-slate-400"
            data-testid="tab-dealer-management"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Dealer Management
          </TabsTrigger>
        </TabsList>

        {/* Staff Directory Tab */}
        <TabsContent value="staff" className="space-y-6 mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{staffStats.total}</p>
                    <p className="text-xs text-slate-400">Total Staff</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <UserCheck className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{staffStats.active}</p>
                    <p className="text-xs text-slate-400">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Shield className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{staffStats.dealers}</p>
                    <p className="text-xs text-slate-400">Dealers/RMs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/20">
                    <UserX className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{staffStats.inactive}</p>
                    <p className="text-xs text-slate-400">Inactive</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="card-dark">
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 input-vault"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-full sm:w-48 input-vault">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Staff Table */}
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white">Staff Directory</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : filteredStaff.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No staff members found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Name</TableHead>
                        <TableHead className="text-slate-300">Email</TableHead>
                        <TableHead className="text-slate-300">Phone</TableHead>
                        <TableHead className="text-slate-300">Role</TableHead>
                        <TableHead className="text-slate-300">RM Code</TableHead>
                        <TableHead className="text-slate-300">Dealer Code</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                        <TableHead className="text-slate-300">Created</TableHead>
                        <TableHead className="text-slate-300 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStaff.map((member) => (
                        <TableRow key={member.id} className="border-slate-700">
                          <TableCell className="text-white font-medium">{member.name}</TableCell>
                          <TableCell className="text-slate-300">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-500" />
                              {member.email}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {member.phone ? (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-500" />
                                {member.phone}
                              </div>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </TableCell>
                          <TableCell>{getRoleBadges(member)}</TableCell>
                          <TableCell className="text-slate-300 font-mono">
                            {member.rm_code || member.linked_rm_code || <span className="text-slate-500">-</span>}
                          </TableCell>
                          <TableCell className="text-slate-300 font-mono">
                            {member.dealer_code || member.linked_dealer_code || <span className="text-slate-500">-</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge
                                className={
                                  member.is_active
                                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                                }
                              >
                                {member.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              {member.has_login === false && (
                                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                                  No Login
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-400">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {new Date(member.created_at).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Only show actions for users with login accounts */}
                              {member.has_login !== false && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedStaff(member);
                                      setShowResetPasswordDialog(true);
                                    }}
                                    className="text-amber-400 hover:text-amber-300"
                                    title="Reset Password"
                                  >
                                    <KeyRound className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleStatus(member)}
                                    className={member.is_active ? 'text-amber-400 hover:text-amber-300' : 'text-green-400 hover:text-green-300'}
                                  >
                                    {member.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(member)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(member)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RM Management Tab */}
        <TabsContent value="rms" className="space-y-6 mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <Crown className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{rmStats.total}</p>
                    <p className="text-xs text-slate-400">Total RMs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <UserCheck className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{rmStats.active}</p>
                    <p className="text-xs text-slate-400">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Briefcase className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{rmStats.totalDealers}</p>
                    <p className="text-xs text-slate-400">Total Dealers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Link className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{rmStats.linked}</p>
                    <p className="text-xs text-slate-400">Linked Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="card-dark">
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by code, name, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 input-vault"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40 input-vault">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* RM Table */}
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white">RM Directory</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : filteredRms.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Crown className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No RMs found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Code</TableHead>
                        <TableHead className="text-slate-300">Name</TableHead>
                        <TableHead className="text-slate-300">Contact</TableHead>
                        <TableHead className="text-slate-300">Dealers</TableHead>
                        <TableHead className="text-slate-300">Clients</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                        <TableHead className="text-slate-300">User Link</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRms.map((rm) => (
                        <TableRow key={rm.rm_code} className="border-slate-700">
                          <TableCell className="text-white font-mono font-medium">{rm.rm_code}</TableCell>
                          <TableCell className="text-white">{rm.name}</TableCell>
                          <TableCell className="text-slate-300">
                            <div className="space-y-1">
                              {rm.email && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Mail className="w-3 h-3" /> {rm.email}
                                </div>
                              )}
                              {rm.mobile && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Phone className="w-3 h-3" /> {rm.mobile}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-4 h-4 text-blue-400" />
                              <span className="text-white font-medium">{rm.dealer_count || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-green-400" />
                              <span className="text-white font-medium">{rm.client_count || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                rm.status === 'Active'
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                  : 'bg-red-500/20 text-red-400 border-red-500/30'
                              }
                            >
                              {rm.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {rm.linked_user_id ? (
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                                {rm.linked_user_email || 'Linked'}
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                Not Linked
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dealer Management Tab */}
        <TabsContent value="dealers" className="space-y-6 mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <Briefcase className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{dealerStats.total}</p>
                    <p className="text-xs text-slate-400">Total Dealers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <UserCheck className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{dealerStats.active}</p>
                    <p className="text-xs text-slate-400">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{dealerStats.withRm}</p>
                    <p className="text-xs text-slate-400">With RM</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-dark">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Link className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{dealerStats.linked}</p>
                    <p className="text-xs text-slate-400">Linked Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="card-dark">
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by code, name, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 input-vault"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40 input-vault">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={rmFilter} onValueChange={setRmFilter}>
                  <SelectTrigger className="w-full sm:w-48 input-vault">
                    <SelectValue placeholder="RM Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All RMs</SelectItem>
                    <SelectItem value="unassigned">Unassigned (Direct)</SelectItem>
                    {rms.map((rm) => (
                      <SelectItem key={rm.rm_code} value={rm.rm_code}>
                        {rm.name} ({rm.rm_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Dealer Table */}
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="text-white">Dealer Directory</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-green-400" />
                </div>
              ) : filteredDealers.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No dealers found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Code</TableHead>
                        <TableHead className="text-slate-300">Name</TableHead>
                        <TableHead className="text-slate-300">Contact</TableHead>
                        <TableHead className="text-slate-300">Reporting RM</TableHead>
                        <TableHead className="text-slate-300">Clients</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                        <TableHead className="text-slate-300">User Link</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDealers.map((dealer) => (
                        <TableRow key={dealer.dealer_code} className="border-slate-700">
                          <TableCell className="text-white font-mono font-medium">{dealer.dealer_code}</TableCell>
                          <TableCell className="text-white">{dealer.name}</TableCell>
                          <TableCell className="text-slate-300">
                            <div className="space-y-1">
                              {dealer.email && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Mail className="w-3 h-3" /> {dealer.email}
                                </div>
                              )}
                              {dealer.mobile && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Phone className="w-3 h-3" /> {dealer.mobile}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {dealer.reporting_rm_id ? (
                              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                                {dealer.reporting_rm_name || dealer.reporting_rm_id}
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
                                Direct to Principal
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-white font-medium">{dealer.client_count || 0}</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                dealer.status === 'Active'
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                  : 'bg-red-500/20 text-red-400 border-red-500/30'
                              }
                            >
                              {dealer.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {dealer.linked_user_id ? (
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                                {dealer.linked_user_email || 'Linked'}
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                Not Linked
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Staff Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-400" />
              Add New Staff Member
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new account for a Dealer, RM, or other staff member.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                className="input-vault"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                className="input-vault"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                className="input-vault"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Mobile Number (10 digits)</Label>
              <Input
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, phone: value });
                }}
                placeholder="9876543210"
                maxLength={10}
                className="input-vault"
              />
              {formData.phone && formData.phone.length !== 10 && (
                <p className="text-xs text-amber-400">Enter 10 digit mobile number</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Role(s) *</Label>
              <p className="text-xs text-slate-500 mb-2">
                Select one or more roles. RM + Dealer combination is allowed for dual role staff.
              </p>
              <div className="space-y-2 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                {ROLES.map((role) => {
                  const isSelected = formData.roles?.includes(role.value);
                  const isDualRole = DUAL_ROLE_ALLOWED.includes(role.value);
                  
                  return (
                    <div
                      key={role.value}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        isSelected ? 'bg-slate-700/50' : 'hover:bg-slate-700/30'
                      }`}
                      onClick={() => handleRoleToggle(role.value)}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-500'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <Badge className={`${role.color} text-white`}>
                        {role.label}
                      </Badge>
                      {isDualRole && (
                        <span className="text-xs text-slate-500">(Can combine with {role.value === 'rm' ? 'Dealer' : 'RM'})</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {formData.roles?.length > 1 && (
                <p className="text-xs text-blue-400 mt-1">
                  Selected: {formData.roles.map(r => ROLES.find(role => role.value === r)?.label || r).join(' + ')}
                </p>
              )}
            </div>
            
            {formData.roles?.includes('rm') && (
              <div className="space-y-2">
                <Label className="text-slate-300">RM Code *</Label>
                <Input
                  value={formData.rm_code}
                  onChange={(e) => setFormData({ ...formData, rm_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., RM001"
                  className="input-vault"
                />
                <p className="text-xs text-slate-500">This will auto-create an entry in RM Master</p>
              </div>
            )}
            
            {formData.roles?.includes('dealer') && (
              <div className="space-y-2">
                <Label className="text-slate-300">Dealer Code *</Label>
                <Input
                  value={formData.dealer_code}
                  onChange={(e) => setFormData({ ...formData, dealer_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., D001"
                  className="input-vault"
                />
                <p className="text-xs text-slate-500">This will auto-create an entry in Dealer Master</p>
              </div>
            )}
            
            {formData.roles?.includes('dealer') && !formData.roles?.includes('rm') && (
              <div className="space-y-2">
                <Label className="text-slate-300">Reporting To</Label>
                <Select 
                  value={formData.reporting_rm_id || "direct_principal"} 
                  onValueChange={(value) => setFormData({ ...formData, reporting_rm_id: value === "direct_principal" ? "" : value })}
                >
                  <SelectTrigger className="input-vault">
                    <SelectValue placeholder="Select Reporting To" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct_principal">Direct to Principal (No RM)</SelectItem>
                    {rms.map((rm) => (
                      <SelectItem key={rm.rm_code} value={rm.rm_code}>
                        {rm.name} ({rm.rm_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleAddStaff} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-400" />
              Edit Staff Member
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update information for {selectedStaff?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-vault"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-vault"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">New Password (leave blank to keep current)</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter new password"
                className="input-vault"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Mobile Number (10 digits)</Label>
              <Input
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, phone: value });
                }}
                placeholder="9876543210"
                maxLength={10}
                className="input-vault"
              />
              {formData.phone && formData.phone.length !== 10 && (
                <p className="text-xs text-amber-400">Enter 10 digit mobile number</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Role(s) *</Label>
              <p className="text-xs text-slate-500 mb-2">
                Select one or more roles. RM + Dealer combination is allowed for dual role staff.
              </p>
              <div className="space-y-2 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                {ROLES.map((role) => {
                  const isSelected = formData.roles?.includes(role.value);
                  const isDualRole = DUAL_ROLE_ALLOWED.includes(role.value);
                  
                  return (
                    <div
                      key={role.value}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        isSelected ? 'bg-slate-700/50' : 'hover:bg-slate-700/30'
                      }`}
                      onClick={() => handleRoleToggle(role.value)}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-500'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <Badge className={`${role.color} text-white`}>
                        {role.label}
                      </Badge>
                      {isDualRole && (
                        <span className="text-xs text-slate-500">(Can combine with {role.value === 'rm' ? 'Dealer' : 'RM'})</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {formData.roles?.length > 1 && (
                <p className="text-xs text-blue-400 mt-1">
                  Selected: {formData.roles.map(r => ROLES.find(role => role.value === r)?.label || r).join(' + ')}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleUpdateStaff} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit className="w-4 h-4 mr-2" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Delete Staff Member
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <strong>{selectedStaff?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleDeleteStaff} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Modal */}
      <AdminResetPasswordModal
        isOpen={showResetPasswordDialog}
        onClose={() => {
          setShowResetPasswordDialog(false);
          setSelectedStaff(null);
        }}
        token={token}
        targetUser={selectedStaff}
      />
    </div>
  );
};

export default StaffManagementPage;
